/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import { useHistory, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  FilterComponentType,
  LeadFinderSecondaryTab,
  LeadFinderPrimaryTab,
  VisitorByGenderFilter,
} from '../../types';
import {
  INDUSTRY_DATA,
  LOCATION_DATA,
  EMPLOYEE_COUNT_DATA,
  REVENUE_DATA,
  SENIORITY_GROUP_OPTIONS,
  DEPARTMENT_GROUP_OPTIONS,
  COMPANY_PERCENTAGE_RANGE_DATA,
  COMPANY_WEBSITE_VISITORS_RANGE_DATA,
  SCORE_CHANGE_DURATION_DATA,
  SCORE_CHANGE_RANGE_DATA,
  COMPANY_FUNDING_DATE_DATA,
} from '../../helpers/filter-data';

import {
  getFilterValues,
  formatNumericRangeForDisplay,
  decodeLocationV2Entry,
  deletePairedLocationExactMatchFlagsIfEmpty,
  encodeLocationV2Entry,
  getRegionChildrenKeys,
  LOCATION_V2_FILTER_KEYS,
  MULTILEVEL_INSTANCE_KEY_SEP,
  isPositiveCompanyId,
  matchSemanticDatePreset,
} from '../../helpers/helper';
import { getCompanyNameByIdsAPI } from '../../helpers/leads.api';
import { addCompanyMappings } from '../../utils/company-id-name-map';

type CompanyListOption = { id: number; label: string; count?: number };

interface FundingDateDisplayPayload {
  label: string;
  tooltipLabel?: string;
}

/**
 * Converts funding date filter value (preset key or custom range) to display label and optional range tooltip.
 * Mirrors getDateRange logic in useCompanyFinder for preset keys.
 */
function getCompanyFundingDateDisplayPayload(
  value: string,
): FundingDateDisplayPayload {
  const trimmed = (value || '').trim();
  if (!trimmed) return { label: value };

  if (trimmed === 'all time') return { label: 'All Time' };
  if (trimmed.includes(' - ')) {
    const [minStr, maxStr] = trimmed.split(' - ').map((v) => v.trim());
    const filterMin = new Date(minStr);
    const filterMax = new Date(maxStr);
    if (!Number.isNaN(filterMin.getTime()) && !Number.isNaN(filterMax.getTime())) {
      const matchedPreset = matchSemanticDatePreset(filterMin, filterMax, COMPANY_FUNDING_DATE_DATA);
      if (matchedPreset) {
        return { label: matchedPreset.label, tooltipLabel: trimmed };
      }
    }
    return { label: trimmed };
  }

  const getDateRange = (dateValue: string): string | null => {
    const end = new Date();
    const start = new Date();

    if (dateValue === 'last_30_days') {
      start.setDate(start.getDate() - 30);
    } else if (dateValue === 'last_90_days') {
      start.setDate(start.getDate() - 90);
    } else if (dateValue === 'last_6_months') {
      start.setMonth(start.getMonth() - 6);
    } else if (dateValue === 'last_year') {
      start.setFullYear(start.getFullYear() - 1);
    } else {
      return null;
    }

    return `${start.toISOString().split('T')[0]} - ${
      end.toISOString().split('T')[0]
    }`;
  };

  const presetLabel = COMPANY_FUNDING_DATE_DATA.find(
    (item) => item.key === trimmed,
  )?.label;
  const tooltipLabel = getDateRange(trimmed);

  return {
    label: presetLabel || value,
    ...(tooltipLabel && { tooltipLabel }),
  };
}

// Hook for company filter management
export const useCompanyFilter = (
  activeSubTab: LeadFinderSecondaryTab,
  searchCompanies: (payload?: any, performSearch?: boolean) => void,
  companyListsData?: CompanyListOption[],
  signalsListData?: Array<{ key: string; value: string }>,
  newsListData?: Array<{ key: string; value: string }>,
  activeLeadType?: LeadFinderPrimaryTab,
  userEmail?: string,
  triggerSearchWithFilters?: (filters: URLSearchParams) => void,
  options?: {
    initialFilters?: string;
    initialSkipUrlPushForBulkSearch?: boolean;
    syncTabSpecificCompanyFiltersRef?: (filters: URLSearchParams) => void;
    tabSpecificFiltersRef?: any;
  },
) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);

  const externalSkipUrlPush = !!options?.initialSkipUrlPushForBulkSearch;
  const externalFiltersStr = (
    options?.tabSpecificFiltersRef?.current?.company ||
    options?.initialFilters ||
    ''
  ).trim();
  const hasUrlSearch = (location?.search || '').trim().length > 0;

  // Track pending filters that haven't been applied yet
  const [pendingFilters, setPendingFilters] = useState<URLSearchParams>(
    new URLSearchParams(
      hasUrlSearch
        ? location?.search
        : externalSkipUrlPush
        ? externalFiltersStr
        : '',
    ),
  );

  // Ref to track if URL change came from search button click
  const isSearchButtonClickRef = useRef(false);

  const lastAppliedFiltersRef = useRef<string>('');
  const prevActiveLeadTypeRef = useRef<LeadFinderPrimaryTab | undefined>(
    activeLeadType,
  );
  const prevActiveSubTabRef = useRef<LeadFinderSecondaryTab>(activeSubTab);
  const prevPathnameRef = useRef<string>(location?.pathname || '');
  const lastExternalTriggeredFiltersRef = useRef<string>('');
  const userModifiedBulkFiltersRef = useRef(false);

  const [
    skipUrlPushForBulkSearch,
    setSkipUrlPushForBulkSearch,
  ] = useState<boolean>(externalSkipUrlPush);
  const initialFiltersStr = externalFiltersStr;

  const syncCompanyFiltersTabRef = (params: URLSearchParams) => {
    options?.syncTabSpecificCompanyFiltersRef?.(
      new URLSearchParams(params.toString()),
    );
  };

  useEffect(() => {
    const prevPath = prevPathnameRef.current || '';
    const nextPath = location?.pathname || '';
    const wasCompany = prevPath.includes('/v2/leads/company/');
    const isCompany = nextPath.includes('/v2/leads/company/');

    if (wasCompany && !isCompany) {
      lastExternalTriggeredFiltersRef.current = '';
    }

    prevPathnameRef.current = nextPath;
  }, [location?.pathname]);

  // State to store company ID to name mapping (for company_ids filter)
  const [companyIdNameMap, setCompanyIdNameMap] = useState<
    Record<string, string>
  >({});

  // Track which company IDs are currently being fetched (for loading skeletons)
  const [loadingCompanyIds, setLoadingCompanyIds] = useState<Set<string>>(
    new Set(),
  );

  const prevLocationSearchRef = useRef<string>(location?.search || '');

  // Sync pendingFilters with URL when URL changes externally
  useEffect(() => {
    const isCompanyRoute = (location?.pathname || '').includes(
      '/v2/leads/company/',
    );
    if (!isCompanyRoute) {
      isSearchButtonClickRef.current = false;
      prevLocationSearchRef.current = location?.search || '';
      return;
    }
    const urlIsEmpty = !(location?.search || '').trim();
    const prevUrlWasNonEmpty =
      (prevLocationSearchRef.current || '').trim().length > 0;
    if (urlIsEmpty && prevUrlWasNonEmpty) {
      const cleared = new URLSearchParams();
      setPendingFilters(cleared);
      syncCompanyFiltersTabRef(cleared);
      setSkipUrlPushForBulkSearch(false);
      lastAppliedFiltersRef.current = '';
      isSearchButtonClickRef.current = false;
      prevLocationSearchRef.current = location?.search || '';
      return;
    }
    if (urlIsEmpty && !skipUrlPushForBulkSearch) {
      const cleared = new URLSearchParams();
      setPendingFilters(cleared);
      syncCompanyFiltersTabRef(cleared);
      isSearchButtonClickRef.current = false;
      prevLocationSearchRef.current = location?.search || '';
      return;
    }
    if (!isSearchButtonClickRef.current) {
      const nextHasUrlSearch = (location?.search || '').trim().length > 0;
      if (skipUrlPushForBulkSearch && !nextHasUrlSearch) {
        prevLocationSearchRef.current = location?.search || '';
        return;
      }
      if (nextHasUrlSearch && skipUrlPushForBulkSearch) {
        setSkipUrlPushForBulkSearch(false);
        lastAppliedFiltersRef.current = '';
      }
      setPendingFilters(new URLSearchParams(location?.search));
    }
    isSearchButtonClickRef.current = false;
    prevLocationSearchRef.current = location?.search || '';
  }, [location?.search, activeLeadType, skipUrlPushForBulkSearch]);

  useEffect(() => {
    if (activeLeadType && activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return;
    }
    const currentIsCompanyPath = (location?.pathname || '').includes(
      '/v2/leads/company/',
    );
    if (!currentIsCompanyPath) {
      return;
    }
    const urlIsEmpty = (location?.search || '').trim().length === 0;
    if (!urlIsEmpty) {
      return;
    }
    if (!externalFiltersStr) {
      return;
    }

    if (!skipUrlPushForBulkSearch && !externalSkipUrlPush) {
      return;
    }

    const switchedToCompanyTab =
      prevActiveLeadTypeRef.current !== LeadFinderPrimaryTab.COMPANY &&
      activeLeadType === LeadFinderPrimaryTab.COMPANY;
    const prevPath = prevPathnameRef.current || '';
    const enteredCompanyPath =
      !prevPath.includes('/v2/leads/company/') && currentIsCompanyPath;

    if (!skipUrlPushForBulkSearch) {
      if (externalSkipUrlPush) {
        setSkipUrlPushForBulkSearch(true);
      }
    }

    const current = pendingFilters.toString();
    if (!current && userModifiedBulkFiltersRef.current) {
      return;
    }
    const shouldRestoreIntoUi =
      enteredCompanyPath || switchedToCompanyTab || !current;
    if (shouldRestoreIntoUi && current !== externalFiltersStr) {
      const restored = new URLSearchParams(externalFiltersStr);
      setPendingFilters(restored);
      lastAppliedFiltersRef.current = externalFiltersStr;
    }

    const shouldTriggerByExternalRestore =
      currentIsCompanyPath &&
      externalFiltersStr &&
      externalFiltersStr !== lastExternalTriggeredFiltersRef.current;

    const pendingTrim = pendingFilters.toString().trim();
    const externalTrim = (externalFiltersStr || '').trim();
    const pendingHasValuesDiffersFromExternal =
      pendingTrim.length > 0 && pendingTrim !== externalTrim;

    if (
      triggerSearchWithFilters &&
      (enteredCompanyPath ||
        switchedToCompanyTab ||
        (shouldTriggerByExternalRestore &&
          !pendingHasValuesDiffersFromExternal))
    ) {
      const restored = new URLSearchParams(externalFiltersStr);
      lastAppliedFiltersRef.current = externalFiltersStr;
      lastExternalTriggeredFiltersRef.current = externalFiltersStr;
      triggerSearchWithFilters(restored);
    }
  }, [
    activeLeadType,
    location?.pathname,
    location?.search,
    externalSkipUrlPush,
    externalFiltersStr,
    pendingFilters,
    skipUrlPushForBulkSearch,
    triggerSearchWithFilters,
  ]);

  // Restore bulk-search filters into UI + trigger API without touching URL
  useEffect(() => {
    if (activeLeadType && activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return;
    }
    if (!skipUrlPushForBulkSearch) return;
    const urlIsEmpty = (location?.search || '').trim().length === 0;
    if (!urlIsEmpty) return;
    if (!initialFiltersStr) return;

    const switchedToCompanyTab =
      prevActiveLeadTypeRef.current !== LeadFinderPrimaryTab.COMPANY &&
      activeLeadType === LeadFinderPrimaryTab.COMPANY;

    const currentStr = pendingFilters.toString();
    if (!currentStr && userModifiedBulkFiltersRef.current) {
      return;
    }
    const restored = new URLSearchParams(initialFiltersStr);

    if (!currentStr) {
      setPendingFilters(restored);
    }

    if (
      triggerSearchWithFilters &&
      (switchedToCompanyTab ||
        initialFiltersStr !== lastAppliedFiltersRef.current)
    ) {
      lastAppliedFiltersRef.current = initialFiltersStr;
      triggerSearchWithFilters(restored);
    }
  }, [
    activeLeadType,
    skipUrlPushForBulkSearch,
    location?.search,
    initialFiltersStr,
    pendingFilters,
    triggerSearchWithFilters,
  ]);

  useEffect(() => {
    const isSubTabChanged = prevActiveSubTabRef.current !== activeSubTab;
    prevActiveSubTabRef.current = activeSubTab;
    if (!isSubTabChanged) return;
    if (activeLeadType && activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return;
    }
    if (!skipUrlPushForBulkSearch) return;
    if (!triggerSearchWithFilters) return;
    if (!initialFiltersStr) return;
    const urlIsEmpty = (location?.search || '').trim().length === 0;
    if (!urlIsEmpty) return;

    const restored = new URLSearchParams(initialFiltersStr);
    setPendingFilters(restored);
    lastAppliedFiltersRef.current = initialFiltersStr;
    triggerSearchWithFilters(restored);
  }, [
    activeSubTab,
    activeLeadType,
    skipUrlPushForBulkSearch,
    initialFiltersStr,
    location?.search,
    triggerSearchWithFilters,
  ]);

  useEffect(() => {
    prevActiveLeadTypeRef.current = activeLeadType;
  }, [activeLeadType]);

  // Fetch company names by IDs for company_ids filter (for display in collapsed tags)
  useEffect(() => {
    // Only fetch if Company tab is active
    if (activeLeadType && activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return;
    }

    const fetchCompanyNames = async () => {
      const includedIds = pendingFilters
        .getAll('company_ids')
        .map((id) => id.replace(/"/g, ''));
      const excludedIds = pendingFilters
        .getAll('excludeCompany_ids')
        .map((id) => id.replace(/"/g, ''));
      const allIds = [...includedIds, ...excludedIds];

      if (allIds.length === 0) return;

      const companyNameQuery = pendingFilters.get('company_name');
      if (companyNameQuery != null) {
        const trimmedCompanyName = String(companyNameQuery).trim();
        if (trimmedCompanyName.length < 3) {
          return;
        }
        if (trimmedCompanyName.length > 50) {
          return;
        }
      }

      // Find positive company IDs not yet in the map (skip names like "0")
      const idsToFetch = allIds.filter(
        (id) => isPositiveCompanyId(id) && !companyIdNameMap[id],
      );

      if (idsToFetch.length === 0) return;

      setLoadingCompanyIds((prev) => {
        const next = new Set(prev);
        idsToFetch.forEach((id) => next.add(id));
        return next;
      });

      try {
        const res = await getCompanyNameByIdsAPI({
          companyIds: idsToFetch.map(Number),
        });

        if (res?.payload && Array.isArray(res.payload)) {
          const newMap: Record<string, string> = {};
          res.payload.forEach((company: any) => {
            const id =
              company?.companyId ??
              company?.company_id ??
              company?.id ??
              company?.companyID;
            const name =
              company?.companyName ??
              company?.company_name ??
              company?.name ??
              company?.companyNAME;

            if (id != null && typeof name === 'string' && name.trim() !== '') {
              newMap[String(id)] = name;
            }
          });

          // Add fallback for IDs we requested but API didn't return (avoids infinite re-fetch loop)
          idsToFetch.forEach((id) => {
            if (!newMap[id]) {
              newMap[id] = `${id}`;
            }
          });

          setCompanyIdNameMap((prev) => ({ ...prev, ...newMap }));
        }
      } catch (error) {
        // Silently fail - tags will show IDs as fallback
        // eslint-disable-next-line no-console
        console.error('Failed to fetch company names:', error);
      } finally {
        setLoadingCompanyIds((prev) => {
          const next = new Set(prev);
          idsToFetch.forEach((id) => next.delete(id));
          return next;
        });
      }
    };

    fetchCompanyNames();
  }, [pendingFilters, activeLeadType, companyIdNameMap]);

  // Persist companyIdNameMap to storage for recent search display
  useEffect(() => {
    if (!userEmail || Object.keys(companyIdNameMap).length === 0) return;
    addCompanyMappings(userEmail, companyIdNameMap);
  }, [userEmail, companyIdNameMap]);

  const handleSetUniqueKeys = ({
    filterKey,
    uniqueKeys,
    paramsToRead = pendingFilters,
    typeConfig,
  }: {
    filterKey: string;
    uniqueKeys: any;
    paramsToRead?: URLSearchParams;
    typeConfig?: { name: string; list: any[] };
  }) => {
    // Skip flag parameters
    if (
      filterKey.endsWith('_exactMatch') ||
      filterKey.endsWith('_isUnknown') ||
      filterKey.endsWith('_shouldSearchPast')
    ) {
      return;
    }

    const isExclude = filterKey.toLowerCase().startsWith('exclude');

    paramsToRead
      .getAll(filterKey)
      ?.map((item) => item.replace(/"/g, ''))
      ?.forEach((param) => {
        switch (filterKey) {
          case 'company_hq_location':
          case 'excludeCompany_hq_location': {
            const locationValues = getFilterValues([param], LOCATION_DATA);
            uniqueKeys.add({
              key: filterKey,
              label: locationValues?.length > 0 ? locationValues[0] : param,
              value: param,
              isExclude,
            });
            break;
          }
          case 'company_hq_location_v2':
          case 'excludeCompany_hq_location_v2': {
            const decoded = decodeLocationV2Entry(param);
            let locationLabel = param;
            if (decoded) {
              if (
                decoded.type === 'Country' &&
                decoded.value.includes(MULTILEVEL_INSTANCE_KEY_SEP)
              ) {
                const sepIdx = decoded.value.indexOf(
                  MULTILEVEL_INSTANCE_KEY_SEP,
                );
                locationLabel = decoded.value.slice(
                  sepIdx + MULTILEVEL_INSTANCE_KEY_SEP.length,
                );
              } else {
                locationLabel = decoded.value;
              }
            }
            uniqueKeys.add({
              key: filterKey,
              label: locationLabel,
              value: param,
              isExclude,
            });
            break;
          }
          case 'company_industry':
          case 'excludeCompany_industry': {
            const lookupParam = param.includes(MULTILEVEL_INSTANCE_KEY_SEP)
              ? param.slice(
                  param.indexOf(MULTILEVEL_INSTANCE_KEY_SEP) +
                    MULTILEVEL_INSTANCE_KEY_SEP.length,
                )
              : param;
            const industryValues = getFilterValues(
              [lookupParam],
              INDUSTRY_DATA,
            );
            uniqueKeys.add({
              key: filterKey,
              label:
                industryValues?.length > 0 ? industryValues[0] : lookupParam,
              value: param,
              isExclude,
            });
            break;
          }
          case 'company_list':
          case 'excludeCompany_list': {
            const listId = parseInt(param, 10);
            const companyListFromApi = companyListsData?.find(
              (list) => list.id === listId,
            );
            if (companyListFromApi) {
              const label =
                `${companyListFromApi?.label} (${companyListFromApi?.count})` ||
                param;
              uniqueKeys.add({
                key: filterKey,
                label,
                value: param,
                isExclude,
              });
            } else {
              uniqueKeys.add({
                key: filterKey,
                label: param,
                value: param,
                isExclude,
                isLoading: true,
              });
            }
            break;
          }
          case 'company_annual_revenue':
          case 'source_5_annual_revenue':
          case 'company_funding_amount':
          case 'last_funding_round_amount_raised':
          case 'base_salary_salary_median': {
            const fundingValues = getFilterValues([param], REVENUE_DATA);
            let fundingDisplayLabel = param;
            if (fundingValues?.length === 0) {
              if (param.includes('-')) {
                const [min, max] = param.split('-');
                const minNum = parseInt(min, 10);
                const maxNum = parseInt(max, 10);
                if (!Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
                  const formattedMin = minNum.toLocaleString('en-US');
                  const formattedMax = maxNum.toLocaleString('en-US');
                  if (formattedMin === '0' && formattedMax !== '0') {
                    fundingDisplayLabel = `<$${formattedMax}`;
                  } else if (formattedMin !== '0' && formattedMax === '') {
                    fundingDisplayLabel = `$${formattedMin}+`;
                  } else {
                    fundingDisplayLabel = `$${formattedMin} - $${formattedMax}`;
                  }
                } else if (!Number.isNaN(minNum) && Number.isNaN(maxNum)) {
                  fundingDisplayLabel = `$${minNum.toLocaleString('en-US')}+`;
                } else if (Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
                  fundingDisplayLabel = `<$${maxNum.toLocaleString('en-US')}`;
                } else if (Number.isNaN(minNum) && Number.isNaN(maxNum)) {
                  fundingDisplayLabel = `$${param}`;
                }
              } else {
                const numValue = parseInt(param, 10);
                if (!Number.isNaN(numValue)) {
                  fundingDisplayLabel = `$${numValue.toLocaleString('en-US')}+`;
                }
              }
            } else {
              const [firstValue] = fundingValues;
              fundingDisplayLabel = firstValue;
            }
            if (
              filterKey === 'company_funding_amount' ||
              filterKey === 'last_funding_round_amount_raised'
            ) {
              fundingDisplayLabel = `Funding Amount: ${fundingDisplayLabel}`;
            }
            uniqueKeys.add({
              key: filterKey,
              label: fundingDisplayLabel,
              value: param,
              isExclude,
            });
            break;
          }
          case 'company_size': {
            const employeeCountValues = getFilterValues(
              [param],
              EMPLOYEE_COUNT_DATA,
            );
            uniqueKeys.add({
              key: filterKey,
              label:
                employeeCountValues?.length > 0
                  ? employeeCountValues[0]
                  : param,
              value: param,
              isExclude,
            });
            break;
          }
          case 'company_founded_year': {
            // Convert relative year keys to actual year range for display
            const currentYear = new Date().getFullYear();
            const trimmedParam = param.trim();
            let displayLabel = param;

            // Handle custom "Before Year" and "After Year" options
            if (trimmedParam.startsWith('-')) {
              const year = parseInt(trimmedParam.slice(1), 10);
              if (!Number.isNaN(year) && year >= 1900 && year <= currentYear) {
                displayLabel = `Before ${year}`;
              }
            } else if (trimmedParam.endsWith('-')) {
              const year = parseInt(trimmedParam.slice(0, -1), 10);
              if (!Number.isNaN(year) && year >= 1900 && year <= currentYear) {
                displayLabel = `After ${year}`;
              }
            } else if (trimmedParam.endsWith('+')) {
              // Handle "20+" format
              const yearsPast = parseInt(trimmedParam.replace('+', ''), 10);
              if (!Number.isNaN(yearsPast)) {
                const minYear = currentYear - yearsPast;
                displayLabel = `${minYear}-${currentYear}`;
              }
            } else if (trimmedParam.includes('-')) {
              // Check if it's already a year range (4-digit years)
              const parts = trimmedParam.split('-');
              if (parts.length === 2) {
                const part1 = parseInt(parts[0].trim(), 10);
                const part2 = parseInt(parts[1].trim(), 10);
                // If both parts are 4-digit years, it's already converted
                if (
                  !Number.isNaN(part1) &&
                  !Number.isNaN(part2) &&
                  part1 >= 1900 &&
                  part1 <= currentYear &&
                  part2 >= 1900 &&
                  part2 <= currentYear
                ) {
                  displayLabel = trimmedParam; // Already a year range
                } else {
                  // Handle "1-3" or "1 -3" format - convert to year range
                  const yearParts = parts.map((p) => parseInt(p.trim(), 10));
                  if (yearParts.length === 2 && !yearParts.some(Number.isNaN)) {
                    const maxYear = currentYear; // First number = current year
                    const minYear = currentYear - yearParts[1]; // Second number = years in past
                    displayLabel = `${minYear}-${maxYear}`;
                  }
                }
              }
            }

            uniqueKeys.add({
              key: filterKey,
              label: displayLabel,
              value: param, // Keep original value for URL
              isExclude,
            });
            break;
          }
          case 'company_bootstrapped':
            if (param === '0') {
              uniqueKeys.add({
                key: filterKey,
                label: 'Is Not Bootstrapped',
                value: param,
                isExclude,
              });
            } else {
              uniqueKeys.add({
                key: filterKey,
                label: 'Is Bootstrapped',
                value: param,
                isExclude,
              });
            }
            break;
          case 'is_b2b':
            if (param === '0') {
              uniqueKeys.add({
                key: filterKey,
                label: 'Is Not B2B',
                value: param,
                isExclude,
              });
            } else {
              uniqueKeys.add({
                key: filterKey,
                label: 'Is B2B',
                value: param,
                isExclude,
              });
            }
            break;
          case 'ownership_status':
            uniqueKeys.add({
              key: filterKey,
              label: param,
              value: param,
              isExclude,
            });
            break;
          case 'signals': {
            const signalItem = signalsListData?.find((s) => s.key === param);
            uniqueKeys.add({
              key: filterKey,
              label: signalItem?.value || param,
              value: param,
              isExclude: false,
            });
            break;
          }
          case 'news': {
            const newsItem = newsListData?.find((n) => n.key === param);
            uniqueKeys.add({
              key: filterKey,
              label: newsItem?.value || param,
              value: param,
              isExclude: false,
            });
            break;
          }
          case 'funding_rounds_name':
          case 'last_funding_round_name': {
            const fundingRoundLabel = `Round: ${param}`;
            uniqueKeys.add({
              key: filterKey,
              label: fundingRoundLabel,
              isExclude,
              value: param,
            });
            break;
          }
          case 'funding_rounds_announced_date':
          case 'last_funding_round_announced_date': {
            const displayPayload = getCompanyFundingDateDisplayPayload(param);
            uniqueKeys.add({
              key: filterKey,
              label: displayPayload.label,
              value: param,
              isExclude,
              ...(displayPayload.tooltipLabel && {
                tooltipLabel: displayPayload.tooltipLabel,
              }),
            });
            break;
          }
          case 'company_ids':
          case 'excludeCompany_ids': {
            const hasName = !!companyIdNameMap[param];
            const isLoading = loadingCompanyIds.has(param);

            uniqueKeys.add({
              key: filterKey,
              label: hasName ? companyIdNameMap[param] : param,
              value: param,
              isExclude,
              ...(isLoading && { isLoading: true }),
            });
            break;
          }
          case 'rank_global': {
            const rankDisplayValues = getFilterValues(
              [param],
              COMPANY_WEBSITE_VISITORS_RANGE_DATA,
            );
            const rankLabel =
              rankDisplayValues?.length > 0
                ? rankDisplayValues[0]
                : formatNumericRangeForDisplay(param);
            uniqueKeys.add({
              key: filterKey,
              label: rankLabel,
              value: param,
              isExclude,
            });
            break;
          }
          default: {
            // Sub-filters with name + list (e.g. employee_reviews): show "Filter Name: value"
            if (typeConfig?.name && typeConfig?.list) {
              const displayValues = getFilterValues([param], typeConfig.list);
              const displayValue =
                displayValues?.length > 0 ? displayValues[0] : param;
              uniqueKeys.add({
                key: filterKey,
                label: `${typeConfig.name}: ${displayValue}`,
                value: param,
                isExclude,
              });
              break;
            }
            // Check if this is a group dropdown param key (ecbbs_* or ecbyd_*)
            const groupOption =
              SENIORITY_GROUP_OPTIONS.find(
                (opt) => opt.paramKey === filterKey,
              ) ||
              DEPARTMENT_GROUP_OPTIONS.find(
                (opt) => opt.paramKey === filterKey,
              );
            if (groupOption) {
              uniqueKeys.add({
                key: filterKey,
                label: `${groupOption.label}: ${param}`,
                value: param,
                isExclude,
              });
            } else {
              uniqueKeys.add({
                key: filterKey,
                label: param,
                isExclude,
                value: param,
              });
            }
          }
        }
      });
  };

  const handleGetAllKeysByFilterFromQuery = (filter: any) => {
    const uniqueKeys = new Set();

    // Visitor by Gender: show single combined chip (e.g. "Male: 10-25%") instead of two separate chips
    if (filter.key === 'visits_breakdown_by_gender') {
      const typeParam = pendingFilters.get('visits_breakdown_by_gender_type');
      const rangeParam = pendingFilters.get('visits_breakdown_by_gender_range');
      const typeValid =
        typeParam &&
        typeParam !== '' &&
        typeParam !== 'select' &&
        (typeParam === 'male' || typeParam === 'female');
      const rangeValid =
        rangeParam && rangeParam !== '' && rangeParam !== 'select';
      if (typeValid && rangeValid) {
        const genderLabel =
          typeParam.charAt(0).toUpperCase() + typeParam.slice(1);
        const rangeLabels = getFilterValues(
          [rangeParam],
          COMPANY_PERCENTAGE_RANGE_DATA,
        );
        const rangeLabel =
          rangeLabels?.length > 0
            ? rangeLabels[0]
            : formatNumericRangeForDisplay(rangeParam);
        uniqueKeys.add({
          key: 'visits_breakdown_by_gender',
          label: `${genderLabel}: ${rangeLabel}`,
          value: `${typeParam}:${rangeParam}`,
          isCombined: true,
          combinedKeys: [
            { key: 'visits_breakdown_by_gender_type', value: typeParam },
            { key: 'visits_breakdown_by_gender_range', value: rangeParam },
          ],
        });
        return uniqueKeys;
      }
      return uniqueKeys;
    }

    // Product Review Score Change: show single combined chip (e.g. "increased by 1.0-1.5 quarterly")
    if (filter.key === 'product_reviews_score_change') {
      const durationParam = pendingFilters.get(
        'product_reviews_score_change_duration',
      );
      const directionParam = pendingFilters.get(
        'product_reviews_score_change_direction',
      );
      const rangeParam = pendingFilters.get(
        'product_reviews_score_change_points_range',
      );
      const durationValid =
        durationParam && durationParam !== '' && durationParam !== 'select';
      const directionValid =
        directionParam && directionParam !== '' && directionParam !== 'select';
      const rangeValid =
        rangeParam && rangeParam !== '' && rangeParam !== 'select';
      if (durationValid && directionValid && rangeValid) {
        let directionVerb = 'Changed';
        if (directionParam === 'up') directionVerb = 'Increased';
        else if (directionParam === 'down') directionVerb = 'Decreased';
        const durationLabels = getFilterValues(
          [durationParam],
          SCORE_CHANGE_DURATION_DATA,
        );
        const durationLabel =
          durationLabels?.length > 0 ? durationLabels[0] : durationParam;
        const rangeLabels = getFilterValues(
          [rangeParam],
          SCORE_CHANGE_RANGE_DATA,
        );
        const rangeLabel =
          rangeLabels?.length > 0 ? rangeLabels[0] : rangeParam;
        const label = `${directionVerb} by ${rangeLabel} ${durationLabel}`;
        uniqueKeys.add({
          key: 'product_reviews_score_change',
          label,
          value: `${directionParam}:${rangeParam}:${durationParam}`,
          isCombined: true,
          combinedKeys: [
            {
              key: 'product_reviews_score_change_duration',
              value: durationParam,
            },
            {
              key: 'product_reviews_score_change_direction',
              value: directionParam,
            },
            {
              key: 'product_reviews_score_change_points_range',
              value: rangeParam,
            },
          ],
        });
      }
      return uniqueKeys;
    }

    if (filter.type?.length > 0) {
      filter.type.forEach((type) => {
        // Handle GROUP_DROPDOWN type specially
        if (
          type.value === FilterComponentType.GROUP_DROPDOWN &&
          type.groupOptions
        ) {
          // For group dropdowns, iterate all sub-param keys
          type.groupOptions.forEach((option) => {
            handleSetUniqueKeys({ filterKey: option.paramKey, uniqueKeys });
          });
          return;
        }

        // Handle GROUP_DROPDOWN with API-fetched options (e.g. Visitor by Country)
        if (
          type.value === FilterComponentType.GROUP_DROPDOWN &&
          type.fetchGroupOptionsFromApi &&
          type.key
        ) {
          const prefix = `${type.key}_`;
          const percentagePrefix = `${type.key}_percentage_`;
          pendingFilters.forEach((_, paramKey) => {
            const match = paramKey.match(
              new RegExp(
                `^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`,
              ),
            );
            if (match) {
              const index = match[1];
              const countryValue = pendingFilters.get(paramKey);
              const percentageValue = pendingFilters.get(
                percentagePrefix + index,
              );
              if (
                countryValue &&
                countryValue !== '' &&
                countryValue !== 'select'
              ) {
                let rangeLabel = '';

                if (percentageValue && percentageValue !== 'select') {
                  if (type.key === 'base_salary_title') {
                    rangeLabel = `: $${percentageValue}`;
                  } else {
                    rangeLabel = `: ${percentageValue}`;
                  }
                }
                uniqueKeys.add({
                  key: paramKey,
                  label: `${countryValue}${rangeLabel}`,
                  value: countryValue,
                });
              }
            }
          });
          return;
        }

        if (type.key) {
          const isUnknownValue = pendingFilters.get(`${type.key}_isUnknown`);

          if (isUnknownValue === '1') {
            uniqueKeys.add({
              key: `${type.key}_isUnknown`,
              label: 'Unknown',
              value: '1',
              isFlag: true,
              flagType: 'isUnknown',
            });
            return;
          }

          handleSetUniqueKeys({
            filterKey: type?.key,
            uniqueKeys,
            typeConfig:
              type?.name && type?.list
                ? { name: type.name, list: type.list }
                : undefined,
          });

          const capitalizeExcludeKey = `exclude${type.key
            .charAt(0)
            .toUpperCase()}${type.key.slice(1)}`;
          handleSetUniqueKeys({
            filterKey: capitalizeExcludeKey,
            uniqueKeys,
          });

          if (type.key === 'company_hq_location') {
            const v2Keys =
              LOCATION_V2_FILTER_KEYS[
                type.key as keyof typeof LOCATION_V2_FILTER_KEYS
              ];
            handleSetUniqueKeys({
              filterKey: v2Keys.v2Include,
              uniqueKeys,
            });
            handleSetUniqueKeys({
              filterKey: v2Keys.v2Exclude,
              uniqueKeys,
            });
          }

          // Special case: "Company Name/Domain" filter UI uses `company_ids` (autocomplete),
          // but bulk search & URL can also carry `company_name` filters. Show them under the same filter section.
          if (type.key === 'company_ids') {
            handleSetUniqueKeys({ filterKey: 'company_name', uniqueKeys });
            handleSetUniqueKeys({
              filterKey: 'excludeCompany_name',
              uniqueKeys,
            });

            const companyNameExactMatchValue = pendingFilters.get(
              'company_name_exactMatch',
            );
            if (companyNameExactMatchValue === '1') {
              uniqueKeys.add({
                key: 'company_name_exactMatch',
                label: 'Exact Match',
                value: '1',
                isFlag: true,
                flagType: 'exactMatch',
              });
            }
          }

          if (isUnknownValue === '0') {
            uniqueKeys.add({
              key: `${type.key}_isUnknown`,
              label: 'Known',
              value: '0',
              isFlag: true,
              flagType: 'isUnknown',
            });
          }

          const exactMatchValue = pendingFilters.get(`${type.key}_exactMatch`);
          const v2LocationKeysForType =
            type.key === 'company_hq_location'
              ? LOCATION_V2_FILTER_KEYS[
                  type.key as keyof typeof LOCATION_V2_FILTER_KEYS
                ]
              : null;
          const v2ExactMatchValue = v2LocationKeysForType
            ? pendingFilters.get(
                `${v2LocationKeysForType.v2Include}_exactMatch`,
              )
            : null;
          if (exactMatchValue === '1' || v2ExactMatchValue === '1') {
            uniqueKeys.add({
              key: `${type.key}_exactMatch`,
              label: 'Exact Match',
              value: '1',
              isFlag: true,
              flagType: 'exactMatch',
            });
          }
        }
      });
    }
    return uniqueKeys;
  };

  const handleIndividualTag = (tag: any) => {
    const getParamKeyVariants = (key: string): string[] => {
      if (!key) return [];
      return Array.from(new Set([key, key.toLowerCase()])).filter(Boolean);
    };
    const removeValueFromParams = (
      params: URLSearchParams,
      key: string,
      value: string,
    ) => {
      const normalize = (v: string) => {
        try {
          return decodeURIComponent(String(v)).replace(/"/g, '').trim();
        } catch (_e) {
          return String(v).replace(/"/g, '').trim();
        }
      };
      const target = normalize(value);
      const paramValues = params.getAll(key);
      const updatedValues = paramValues.filter((v) => normalize(v) !== target);
      params.delete(key);
      updatedValues.forEach((v) => params.append(key, v));
      return params;
    };

    // Combined chip (e.g. Visitor by Gender): remove all related params
    if (tag?.isCombined && tag?.combinedKeys?.length) {
      userModifiedBulkFiltersRef.current = true;
      const newPendingFilters = new URLSearchParams(pendingFilters.toString());
      const nextUrlParams = new URLSearchParams(location?.search || '');
      tag.combinedKeys.forEach(
        ({ key: combinedKey, value: combinedValue }: any) => {
          removeValueFromParams(newPendingFilters, combinedKey, combinedValue);
          removeValueFromParams(nextUrlParams, combinedKey, combinedValue);
        },
      );

      tag.combinedKeys.forEach(({ key: combinedKey }: any) => {
        if (newPendingFilters.getAll(combinedKey).length === 0) {
          newPendingFilters.delete(`${combinedKey}_exactMatch`);
          newPendingFilters.delete(`${combinedKey}_isUnknown`);
          newPendingFilters.delete(`${combinedKey}_shouldSearchPast`);
        }
        if (nextUrlParams.getAll(combinedKey).length === 0) {
          nextUrlParams.delete(`${combinedKey}_exactMatch`);
          nextUrlParams.delete(`${combinedKey}_isUnknown`);
          nextUrlParams.delete(`${combinedKey}_shouldSearchPast`);
        }
      });

      setPendingFilters(newPendingFilters);
      syncCompanyFiltersTabRef(newPendingFilters);
      if (skipUrlPushForBulkSearch && triggerSearchWithFilters) {
        const newFiltersStr = newPendingFilters.toString();
        const remainingCombinedBulk = new URLSearchParams(newFiltersStr);
        remainingCombinedBulk.delete('page');
        remainingCombinedBulk.delete('start');
        remainingCombinedBulk.delete('take');
        if (
          remainingCombinedBulk.toString().length > 0 &&
          newFiltersStr !== lastAppliedFiltersRef.current
        ) {
          lastAppliedFiltersRef.current = newFiltersStr;
          triggerSearchWithFilters(newPendingFilters);
        }
        return;
      }
      history.push({
        search: nextUrlParams.toString(),
      });
      setPendingFilters(new URLSearchParams(nextUrlParams.toString()));
      syncCompanyFiltersTabRef(nextUrlParams);
      const remainingCombinedUrl = new URLSearchParams(
        nextUrlParams.toString(),
      );
      remainingCombinedUrl.delete('page');
      remainingCombinedUrl.delete('start');
      remainingCombinedUrl.delete('take');
      if (remainingCombinedUrl.toString().length > 0) {
        searchCompanies(null, false);
      }
      return;
    }

    const normalizedTagValue = (tag?.value ?? '').toString().replace(/"/g, '');

    const v2LocationKeyNames = Object.values(
      LOCATION_V2_FILTER_KEYS,
    ).flatMap((k) => [k.v2Include, k.v2Exclude]);
    const extraV2ValuesToRemove: string[] = [];
    if (v2LocationKeyNames.includes(tag?.key)) {
      const decodedTag = decodeLocationV2Entry(normalizedTagValue);
      if (decodedTag?.type === 'Region') {
        getRegionChildrenKeys(decodedTag.value, LOCATION_DATA).forEach(
          (child) => {
            extraV2ValuesToRemove.push(encodeLocationV2Entry('Country', child));
          },
        );
      }
    }

    // Always read URL params fresh (avoid stale closure if location.search changed)
    const urlParams = new URLSearchParams(location?.search || '');
    const keysToTry = getParamKeyVariants(tag?.key);
    const normalize = (v: string) => {
      try {
        return decodeURIComponent(String(v)).replace(/"/g, '').trim();
      } catch (_e) {
        return String(v).replace(/"/g, '').trim();
      }
    };
    const existsInUrl = keysToTry.some((k) =>
      urlParams
        .getAll(k)
        .some((v) => normalize(v) === normalize(normalizedTagValue)),
    );

    if (tag?.isFlag) {
      const extraFlagKeys: string[] = [];
      if (
        tag?.flagType === 'exactMatch' &&
        tag?.key === 'company_hq_location_exactMatch'
      ) {
        const base = tag.key.replace(/_exactMatch$/, '');
        const v2Keys =
          LOCATION_V2_FILTER_KEYS[base as keyof typeof LOCATION_V2_FILTER_KEYS];
        if (v2Keys) extraFlagKeys.push(`${v2Keys.v2Include}_exactMatch`);
      }

      const needsPendingUpdate =
        pendingFilters.has(tag?.key) ||
        extraFlagKeys.some((k) => pendingFilters.has(k));
      if (needsPendingUpdate) {
        userModifiedBulkFiltersRef.current = true;
        const newPendingFilters = new URLSearchParams(
          pendingFilters.toString(),
        );
        newPendingFilters.delete(tag?.key);
        extraFlagKeys.forEach((k) => newPendingFilters.delete(k));
        setPendingFilters(newPendingFilters);
        syncCompanyFiltersTabRef(newPendingFilters);

        if (skipUrlPushForBulkSearch && triggerSearchWithFilters) {
          const newFiltersStr = newPendingFilters.toString();
          const remainingFlagBulk = new URLSearchParams(newFiltersStr);
          remainingFlagBulk.delete('page');
          remainingFlagBulk.delete('start');
          remainingFlagBulk.delete('take');
          if (remainingFlagBulk.toString().length > 0) {
            lastAppliedFiltersRef.current = newFiltersStr;
            triggerSearchWithFilters(newPendingFilters);
          }
          return;
        }
      }

      const needsUrlUpdate =
        urlParams.has(tag?.key) || extraFlagKeys.some((k) => urlParams.has(k));
      if (needsUrlUpdate) {
        urlParams.delete(tag?.key);
        extraFlagKeys.forEach((k) => urlParams.delete(k));
        history.push({
          search: urlParams.toString(),
        });
        const nextFlag = new URLSearchParams(urlParams.toString());
        setPendingFilters(nextFlag);
        syncCompanyFiltersTabRef(nextFlag);
        const remainingFlagUrl = new URLSearchParams(urlParams.toString());
        remainingFlagUrl.delete('page');
        remainingFlagUrl.delete('start');
        remainingFlagUrl.delete('take');
        if (remainingFlagUrl.toString().length > 0) {
          searchCompanies(null, false);
        }
      }
      return;
    }

    {
      userModifiedBulkFiltersRef.current = true;
      const newPendingFilters = new URLSearchParams(pendingFilters.toString());
      const before = newPendingFilters.toString();
      keysToTry.forEach((k) => {
        removeValueFromParams(newPendingFilters, k, normalizedTagValue);
        extraV2ValuesToRemove.forEach((v) =>
          removeValueFromParams(newPendingFilters, k, v),
        );
      });

      if (newPendingFilters.toString() === before) {
        keysToTry.forEach((k) => {
          const vals = newPendingFilters.getAll(k);
          if (vals.length === 1) {
            newPendingFilters.delete(k);
          }
        });
      }

      // For Visitor by Country dynamic GROUP_DROPDOWN: also remove the percentage param for this row
      const countryRowMatch = tag?.key?.match(
        /^visits_breakdown_by_country_(\d+)$/,
      );
      if (countryRowMatch) {
        newPendingFilters.delete(
          `visits_breakdown_by_country_percentage_${countryRowMatch[1]}`,
        );
      }

      if (newPendingFilters.getAll(tag?.key).length === 0) {
        newPendingFilters.delete(`${tag?.key}_exactMatch`);
        newPendingFilters.delete(`${tag?.key}_isUnknown`);
        newPendingFilters.delete(`${tag?.key}_shouldSearchPast`);
      }
      deletePairedLocationExactMatchFlagsIfEmpty(newPendingFilters, tag?.key);

      const after = newPendingFilters.toString();
      if (after !== before) {
        setPendingFilters(newPendingFilters);
        syncCompanyFiltersTabRef(newPendingFilters);

        if (skipUrlPushForBulkSearch && triggerSearchWithFilters) {
          const remainingRegularBulk = new URLSearchParams(after);
          remainingRegularBulk.delete('page');
          remainingRegularBulk.delete('start');
          remainingRegularBulk.delete('take');
          if (remainingRegularBulk.toString().length > 0) {
            lastAppliedFiltersRef.current = after;
            triggerSearchWithFilters(newPendingFilters);
          }
          return;
        }
      }
    }

    if (!skipUrlPushForBulkSearch) {
      const nextUrlParams = new URLSearchParams(location?.search || '');
      const beforeUrl = nextUrlParams.toString();

      keysToTry.forEach((k) => {
        removeValueFromParams(nextUrlParams, k, normalizedTagValue);
        extraV2ValuesToRemove.forEach((v) =>
          removeValueFromParams(nextUrlParams, k, v),
        );
      });

      if (nextUrlParams.toString() === beforeUrl) {
        keysToTry.forEach((k) => {
          const vals = nextUrlParams.getAll(k);
          if (vals.length === 1) {
            nextUrlParams.delete(k);
          }
        });
      }

      // For Visitor by Country dynamic GROUP_DROPDOWN: also remove the percentage param from URL
      const countryRowMatch = tag?.key?.match(
        /^visits_breakdown_by_country_(\d+)$/,
      );
      if (countryRowMatch) {
        nextUrlParams.delete(
          `visits_breakdown_by_country_percentage_${countryRowMatch[1]}`,
        );
      }

      if (nextUrlParams.getAll(tag?.key).length === 0) {
        nextUrlParams.delete(`${tag?.key}_exactMatch`);
        nextUrlParams.delete(`${tag?.key}_isUnknown`);
        nextUrlParams.delete(`${tag?.key}_shouldSearchPast`);
      }
      deletePairedLocationExactMatchFlagsIfEmpty(nextUrlParams, tag?.key);

      if (nextUrlParams.toString() !== beforeUrl || existsInUrl) {
        history.push({
          search: nextUrlParams.toString(),
        });
        setPendingFilters(new URLSearchParams(nextUrlParams.toString()));
        syncCompanyFiltersTabRef(nextUrlParams);
        const remainingRegularUrl = new URLSearchParams(
          nextUrlParams.toString(),
        );
        remainingRegularUrl.delete('page');
        remainingRegularUrl.delete('start');
        remainingRegularUrl.delete('take');
        if (remainingRegularUrl.toString().length > 0) {
          searchCompanies(null, false);
        }
      }
    }
  };

  useEffect(() => {
    if (activeLeadType && activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return;
    }
    if (!skipUrlPushForBulkSearch) return;
    if (!triggerSearchWithFilters) return;
    const urlIsEmpty = (location?.search || '').trim().length === 0;
    if (!urlIsEmpty) return;
    const filtersStr = pendingFilters.toString();
    if (filtersStr === lastAppliedFiltersRef.current) return;
    lastAppliedFiltersRef.current = filtersStr;
    triggerSearchWithFilters(new URLSearchParams(filtersStr));
  }, [
    pendingFilters,
    skipUrlPushForBulkSearch,
    triggerSearchWithFilters,
    location?.search,
    activeLeadType,
  ]);

  const updatePendingFilters = (
    pendingParams: URLSearchParams,
    filterKey: string,
    filterValue: any,
    filterType: string,
    filterExcludeKey?: string,
    showExactMatch?: boolean,
    showKnownUnknown?: number,
    competitorCompanyId?: number,
  ): URLSearchParams => {
    const newParams = new URLSearchParams(pendingParams.toString());

    switch (filterType) {
      case FilterComponentType.SEARCH_INPUT:
        newParams.delete(filterKey);
        if (filterValue !== '') {
          newParams.set(filterKey, filterValue.replace(/"/g, ''));
        }
        if (showExactMatch !== undefined) {
          if (showExactMatch) {
            newParams.set(`${filterKey}_exactMatch`, '1');
          } else {
            newParams.delete(`${filterKey}_exactMatch`);
          }
        }
        break;

      case FilterComponentType.RADIO_BUTTON:
        if (
          filterValue === 1 ||
          filterValue === 0 ||
          (typeof filterValue === 'string' && filterValue !== '')
        ) {
          newParams.set(filterKey, String(filterValue));
        } else {
          newParams.delete(filterKey);
        }
        break;

      case FilterComponentType.DROP_DOWN:
        if (filterValue !== '') {
          newParams.set(filterKey, filterValue);
        } else {
          newParams.delete(filterKey);
        }
        if (showKnownUnknown !== undefined) {
          if (showKnownUnknown === 1) {
            newParams.set(`${filterKey}_isUnknown`, '1');
          } else if (showKnownUnknown === 0) {
            newParams.set(`${filterKey}_isUnknown`, '0');
          } else {
            newParams.delete(`${filterKey}_isUnknown`);
          }
        }
        break;

      case FilterComponentType.MULTILEVEL_CHECKBOX: {
        const excludeParamKey =
          filterExcludeKey ||
          `exclude${filterKey.charAt(0).toUpperCase()}${filterKey.slice(1)}`;

        // Build name→id map before deleting competitors_company_name params
        const oldCompetitorNameIdMap: Record<string, string> = {};
        if (filterKey === 'competitors_company_name') {
          const oldNames = newParams.getAll('competitors_company_name');
          const oldIds = newParams.getAll('competitors_company_id');
          oldNames.forEach((name, i) => {
            const cleanName = name.replace(/"/g, '');
            if (oldIds[i]) {
              oldCompetitorNameIdMap[cleanName] = oldIds[i];
            }
          });
          newParams.delete('competitors_company_id');
        }

        newParams.delete(filterKey);
        newParams.delete(excludeParamKey);

        const normalizeKey = (key: string) => key.replace(/"/g, '');

        const uniqueIncludes = new Set<string>();
        const normalizedIncludes: string[] = [];

        if (filterValue?.includedKeys?.length > 0) {
          filterValue.includedKeys.forEach((includeKey: string) => {
            const cleanKey = normalizeKey(includeKey);
            if (!uniqueIncludes.has(cleanKey)) {
              uniqueIncludes.add(cleanKey);
              normalizedIncludes.push(cleanKey);
            }
          });
        }

        const uniqueExcludes = new Set<string>();
        const normalizedExcludes: string[] = [];

        if (filterValue?.excludedKeys?.length > 0) {
          filterValue.excludedKeys.forEach((excludeKey: string) => {
            const cleanKey = normalizeKey(excludeKey);
            if (
              !uniqueExcludes.has(cleanKey) &&
              !uniqueIncludes.has(cleanKey)
            ) {
              uniqueExcludes.add(cleanKey);
              normalizedExcludes.push(cleanKey);
            }
          });
        }

        const finalIncludes = normalizedIncludes.filter(
          (includeKey) => !uniqueExcludes.has(includeKey),
        );

        finalIncludes.forEach((includeKey) => {
          if (newParams.has(filterKey)) {
            newParams.append(filterKey, includeKey);
          } else {
            newParams.set(filterKey, includeKey);
          }
        });

        // Rebuild competitors_company_id in sync with competitors_company_name
        if (filterKey === 'competitors_company_name') {
          finalIncludes.forEach((includeKey) => {
            let idValue = oldCompetitorNameIdMap[includeKey];
            if (!idValue) {
              // New entry: use competitorCompanyId if provided, else '0'
              idValue = competitorCompanyId ? String(competitorCompanyId) : '0';
            }
            if (newParams.has('competitors_company_id')) {
              newParams.append('competitors_company_id', idValue);
            } else {
              newParams.set('competitors_company_id', idValue);
            }
          });
        }

        normalizedExcludes.forEach((excludeKey) => {
          if (newParams.has(excludeParamKey)) {
            newParams.append(excludeParamKey, excludeKey);
          } else {
            newParams.set(excludeParamKey, excludeKey);
          }
        });

        if (showExactMatch !== undefined) {
          if (showExactMatch) {
            newParams.set(`${filterKey}_exactMatch`, '1');
          } else {
            newParams.delete(`${filterKey}_exactMatch`);
          }
        }

        if (showKnownUnknown !== undefined) {
          if (showKnownUnknown === 1) {
            newParams.set(`${filterKey}_isUnknown`, '1');
          } else if (showKnownUnknown === 0) {
            newParams.set(`${filterKey}_isUnknown`, '0');
          } else {
            newParams.delete(`${filterKey}_isUnknown`);
          }
        }
        break;
      }

      default:
        break;
    }

    return newParams;
  };

  const handleQuery = ({
    filterKey,
    filterValue,
    filterType,
    filterExcludeKey,
    showExactMatch,
    showKnownUnknown,
    onKeyEnter = false,
    onFiltersUpdated,
    competitorCompanyId,
    baseFilters,
  }: {
    filterKey: string;
    filterValue: any;
    filterType: string;
    filterExcludeKey?: string;
    showExactMatch?: boolean;
    showKnownUnknown?: number;
    onKeyEnter?: boolean;
    onFiltersUpdated?: (updatedFilters: URLSearchParams) => void;
    competitorCompanyId?: number;
    baseFilters?: URLSearchParams;
  }) => {
    const startingFilters = baseFilters || pendingFilters;
    const newPendingFilters = updatePendingFilters(
      startingFilters,
      filterKey,
      filterValue,
      filterType,
      filterExcludeKey,
      showExactMatch,
      showKnownUnknown,
      competitorCompanyId,
    );

    setPendingFilters(newPendingFilters);

    if (onFiltersUpdated) {
      onFiltersUpdated(newPendingFilters);
    }

    if (onKeyEnter) {
      isSearchButtonClickRef.current = true;
      const queryString = newPendingFilters.toString();
      const shouldSkipPush = skipUrlPushForBulkSearch;

      if (!shouldSkipPush) {
        history.push({
          search: queryString,
        });
      } else if (triggerSearchWithFilters) {
        const filtersStr = newPendingFilters.toString();
        if (filtersStr !== lastAppliedFiltersRef.current) {
          lastAppliedFiltersRef.current = filtersStr;
          triggerSearchWithFilters(newPendingFilters);
        }
      }
    }
  };

  const handleBulkQuery = (
    bulkQueryInputs: {
      filterKey: string;
      filterValue: any;
      filterType: string;
      filterExcludeKey?: string;
      showExactMatch?: boolean;
      showKnownUnknown?: number;
    }[],
    onFiltersUpdated?: (updatedFilters: URLSearchParams) => void,
    options?: { baseFilters?: URLSearchParams },
  ) => {
    let newPendingFilters = options?.baseFilters || pendingFilters;

    bulkQueryInputs.forEach(
      ({
        filterKey,
        filterValue,
        filterType,
        filterExcludeKey,
        showExactMatch,
        showKnownUnknown,
      }) => {
        newPendingFilters = updatePendingFilters(
          newPendingFilters,
          filterKey,
          filterValue,
          filterType,
          filterExcludeKey,
          showExactMatch,
          showKnownUnknown,
          undefined, // competitorCompanyId not used in bulk operations
        );
      },
    );

    setPendingFilters(newPendingFilters);

    if (onFiltersUpdated) {
      onFiltersUpdated(newPendingFilters);
    }
  };

  const applyPendingFilters = (
    filtersToApply?: URLSearchParams,
    options?: { skipUrlPush?: boolean },
  ) => {
    isSearchButtonClickRef.current = true;
    const filters = filtersToApply || pendingFilters;
    const queryString = filters.toString();
    const shouldSkipPush =
      options?.skipUrlPush === true || skipUrlPushForBulkSearch;
    if (!shouldSkipPush) {
      history.push({
        search: queryString,
      });
    } else if (triggerSearchWithFilters) {
      const filtersStr = filters.toString();
      if (filtersStr === lastAppliedFiltersRef.current) {
        return;
      }
      lastAppliedFiltersRef.current = filtersStr;
      lastExternalTriggeredFiltersRef.current = filtersStr;
      triggerSearchWithFilters(filters);
    }
  };

  const applyExternalBulkFilters = (filters: URLSearchParams) => {
    const next = new URLSearchParams(filters.toString());
    setSkipUrlPushForBulkSearch(true);
    setPendingFilters(next);
    syncCompanyFiltersTabRef(next);
    if (triggerSearchWithFilters) {
      lastAppliedFiltersRef.current = next.toString();
      lastExternalTriggeredFiltersRef.current = next.toString();
      triggerSearchWithFilters(next);
    }
  };

  const hasPendingFilters = () => {
    const params = new URLSearchParams(pendingFilters.toString());
    params.delete('page');
    params.delete('start');
    params.delete('take');
    return params.toString().length > 0;
  };

  const syncPendingFiltersWithUrl = () => {
    setPendingFilters(new URLSearchParams(location?.search));
  };

  const clearPendingFilters = () => {
    setSkipUrlPushForBulkSearch(false);
    setPendingFilters(new URLSearchParams());
    lastAppliedFiltersRef.current = '';
    userModifiedBulkFiltersRef.current = false;
    syncCompanyFiltersTabRef(new URLSearchParams());
  };

  const handleDeleteFilter = (filter, applyFilters = false) => {
    if (!filter) return false;

    const newPendingFilters = new URLSearchParams(pendingFilters.toString());
    filter?.type.forEach((type) => {
      // Handle GROUP_DROPDOWN type specially
      if (
        type.value === FilterComponentType.GROUP_DROPDOWN &&
        type.groupOptions
      ) {
        // Delete all ecbbs_* paramKeys
        type.groupOptions.forEach((option) => {
          newPendingFilters.delete(option.paramKey);
        });
        return;
      }

      // Handle GROUP_DROPDOWN with API-fetched options (e.g. Visitor by Country)
      if (
        type.value === FilterComponentType.GROUP_DROPDOWN &&
        type.fetchGroupOptionsFromApi &&
        type.key
      ) {
        const prefix = `${type.key}_`;
        const percentagePrefix = `${type.key}_percentage_`;
        const keysToDelete: string[] = [];
        newPendingFilters.forEach((_, paramKey) => {
          if (
            paramKey.startsWith(prefix) ||
            paramKey.startsWith(percentagePrefix)
          ) {
            keysToDelete.push(paramKey);
          }
        });
        keysToDelete.forEach((k) => newPendingFilters.delete(k));
        return;
      }

      newPendingFilters.delete(type?.key);
      newPendingFilters.delete(`${type?.key}_exactMatch`);
      newPendingFilters.delete(`${type?.key}_isUnknown`);
      if (type?.key) {
        newPendingFilters.delete(
          `exclude${type.key.charAt(0).toUpperCase()}${type.key.slice(1)}`,
        );
      }

      // If user clears the "Company Name/Domain" filter, also clear bulk-search company name params.
      if (type?.key === 'company_ids') {
        newPendingFilters.delete('company_name');
        newPendingFilters.delete('excludeCompany_name');
        newPendingFilters.delete('company_name_exactMatch');
      }

      // If user clears the "Look alike" company name filter, also clear companion company ID params.
      if (type?.key === 'competitors_company_name') {
        newPendingFilters.delete('competitors_company_id');
      }

      // Also clear paired v2 location params when clearing the HQ location filter.
      if (type?.key === 'company_hq_location') {
        const v2Keys =
          LOCATION_V2_FILTER_KEYS[
            type.key as keyof typeof LOCATION_V2_FILTER_KEYS
          ];
        newPendingFilters.delete(v2Keys.v2Include);
        newPendingFilters.delete(v2Keys.v2Exclude);
        newPendingFilters.delete(`${v2Keys.v2Include}_exactMatch`);
      }
    });

    setPendingFilters(newPendingFilters);
    syncCompanyFiltersTabRef(newPendingFilters);

    if (applyFilters) {
      const remainingFilters = new URLSearchParams(
        newPendingFilters.toString(),
      );
      remainingFilters.delete('page');
      remainingFilters.delete('start');
      remainingFilters.delete('take');

      if (remainingFilters.toString().length === 0) {
        return true;
      }

      applyPendingFilters(newPendingFilters);
      return false;
    }

    return false;
  };

  const isPartialFilterApplied = (() => {
    const ignoredKeys = new Set(['page', 'start', 'take']);

    // ── Product Review Score Change (needs all 3: duration + direction + range) ──
    const scoreChangeKeys = new Set([
      'product_reviews_score_change_duration',
      'product_reviews_score_change_direction',
      'product_reviews_score_change_points_range',
    ]);
    const hasDuration = pendingFilters
      .getAll('product_reviews_score_change_duration')
      .some((v) => v.trim() !== '');
    const hasDirection = pendingFilters
      .getAll('product_reviews_score_change_direction')
      .some((v) => v.trim() !== '');
    const scoreRangeVal = pendingFilters.get(
      'product_reviews_score_change_points_range',
    );
    const hasScoreRange =
      scoreRangeVal !== null && String(scoreRangeVal).trim() !== '';
    const scoreChangeHasAny = hasDuration || hasDirection || hasScoreRange;
    const scoreChangeIsPartial =
      scoreChangeHasAny && !(hasDuration && hasDirection && hasScoreRange);

    // ── Gender (needs both: type + range) ──
    const genderKeys = new Set([
      'visits_breakdown_by_gender_type',
      'visits_breakdown_by_gender_range',
    ]);
    const genderTypeVal = pendingFilters.get('visits_breakdown_by_gender_type');
    const hasValidGenderType =
      genderTypeVal !== null &&
      String(genderTypeVal).trim() !== '' &&
      (genderTypeVal === VisitorByGenderFilter.MALE ||
        genderTypeVal === VisitorByGenderFilter.FEMALE);
    const genderRangeVal = pendingFilters.get(
      'visits_breakdown_by_gender_range',
    );
    const hasGenderRange =
      genderRangeVal !== null && String(genderRangeVal).trim() !== '';
    const genderIsPartial = hasValidGenderType !== hasGenderRange;

    // ── Salary by Job Title (each index needs both: title + percentage) ──
    const salaryKeys = new Set<string>();
    const salaryIndices = new Set<string>();
    pendingFilters.forEach((_, key) => {
      const mTitle = key.match(/^base_salary_title_(\d+)$/);
      if (mTitle) {
        salaryIndices.add(mTitle[1]);
        salaryKeys.add(key);
      }
      const mPercent = key.match(/^base_salary_title_percentage_(\d+)$/);
      if (mPercent) {
        salaryIndices.add(mPercent[1]);
        salaryKeys.add(key);
      }
    });
    let salaryIsPartial = false;
    if (salaryIndices.size > 0) {
      let hasValidSalaryEntry = false;
      salaryIndices.forEach((idx) => {
        const titleVal = pendingFilters.get(`base_salary_title_${idx}`);
        const percentVal = pendingFilters.get(
          `base_salary_title_percentage_${idx}`,
        );
        const validTitle = titleVal && titleVal !== 'select' && titleVal !== '';
        const validRange =
          percentVal &&
          percentVal !== 'select' &&
          String(percentVal).trim() !== '';
        if (validTitle && validRange) hasValidSalaryEntry = true;
      });
      salaryIsPartial = !hasValidSalaryEntry;
    }

    // Collect all partial-only keys for "are there OTHER filters?" check
    const partialOnlyKeys = new Set([
      ...Array.from(scoreChangeKeys),
      ...Array.from(genderKeys),
      ...Array.from(salaryKeys),
    ]);

    const hasOtherFilters = (() => {
      let found = false;
      pendingFilters.forEach((value, key) => {
        if (
          !partialOnlyKeys.has(key) &&
          !ignoredKeys.has(key) &&
          value.trim() !== ''
        ) {
          found = true;
        }
      });
      return found;
    })();

    // Disable only when a partial filter is the sole filter applied
    if (hasOtherFilters) return false;
    return scoreChangeIsPartial || genderIsPartial || salaryIsPartial;
  })();

  const restoreFilterUI = (params: URLSearchParams) => {
    setSkipUrlPushForBulkSearch(true);
    setPendingFilters(new URLSearchParams(params.toString()));
  };

  return {
    handleQuery,
    handleDeleteFilter,
    handleGetAllKeysByFilterFromQuery,
    handleIndividualTag,
    handleBulkQuery,
    applyPendingFilters,
    applyExternalBulkFilters,
    hasPendingFilters,
    syncPendingFiltersWithUrl,
    clearPendingFilters,
    pendingFilters,
    isSearchButtonClickRef,
    companyIdNameMap,
    setCompanyIdNameMap,
    isPartialFilterApplied,
    skipUrlPushForBulkSearch,
    setSkipUrlPushForBulkSearch,
    restoreFilterUI,
  };
};
